import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Button, Spinner } from "reactstrap";
import axios from "axios";
import { toast } from "react-toastify";
import classNames from "classnames";

import ProfileTab from "./components/ProfileTab";
import ListedPlacesTab from "./components/ListedPlacesTab";
import GalleryTab from "./components/GalleryTab";
import RatingsReviewsTab from "./components/RatingsReviewsTab";
import SuggestionsFeedbackTab from "./components/SuggestionsFeedbackTab";
import FeedbackChat from "./FeedbackChat";
import SettlementsTab from "./components/SettlementsTab";
import InvoicesTab from "./components/InvoicesTab";
import RefundsTab from "./components/RefundsTab";

const PartnerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);

  const [partnerDetails, setPartnerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);

  useEffect(() => {
    fetchPartnerDetails();
  }, [id]);

  const fetchPartnerDetails = () => {
    axios
      .get(`/api/admin/v1/partner-details/${id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      })
      .then((res) => setPartnerDetails(res.data.partner))
      .catch(() => toast.error("Error fetching partner details"))
      .finally(() => setLoading(false));
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!partnerDetails) {
    return <div className="text-center mt-5">Partner not found</div>;
  }

  return (
    <div className="partner-details-page">
      <div className="d-flex justify-content-end mb-3">
        <Button
          color="light"
          className="rounded-pill px-4 shadow-sm"
          onClick={() => navigate(-1)}
        >
          ← Back to Partners
        </Button>
      </div>
      <Card className="p-4 mb-4 border-0 shadow-sm rounded-4">
        <div className="d-flex align-items-center">
          <div
            className="bg-light rounded-3 d-flex align-items-center justify-content-center shadow-sm"
            style={{ height: 90, width: 90 }}
          >
            <img
              src={`${process.env.REACT_APP_IMAGE_URL}/${partnerDetails.logo}`}
              alt={partnerDetails.businessName}
              style={{ height: 50, width: 50, objectFit: "contain" }}
            />
          </div>

          <div className="ms-3">
            <h3 className="fw-bold mb-1 text-dark">
              {partnerDetails.businessName}
            </h3>
            <div className="text-muted">{partnerDetails.category}</div>
          </div>
        </div>
        <div className="row mt-4 text-dark">
          <div className="col-md-6 mb-2">
            <strong>Email:</strong> {partnerDetails.email}
          </div>

          <div className="col-md-6 mb-2">
            <strong>Phone:</strong> {partnerDetails.phoneNumber}
          </div>

          <div className="col-md-6 mb-2">
            <strong>Joined:</strong>{" "}
            {new Date(partnerDetails.createdAt).toLocaleDateString()}
          </div>

          <div className="col-md-6 mb-2">
            <strong>Is Verified :</strong>{" "}
            <span
              className="px-3 py-1 rounded-pill text-white ms-1"
              style={{
                background:
                  partnerDetails.isVerified === 1 ? "#28a745" : "#dc3545",
                fontSize: "0.8rem",
                fontWeight: "600",
              }}
            >
              {partnerDetails.isVerified === 1 ? "Verified" : "Not Verified"}
            </span>
            <strong className="ms-3">Status:</strong>{" "}
            <span
              className="px-3 py-1 rounded-pill text-white ms-1"
              style={{
                background: partnerDetails.status === 1 ? "#28a745" : "#dc3545",
                fontSize: "0.8rem",
                fontWeight: "600",
              }}
            >
              {partnerDetails.status === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </Card>
      <div
        className="partner-tabs d-flex border-bottom mb-4"
        style={{ overflowX: "auto" }}
      >
        {[
          "Profile",
          "Places",
          "Gallery",
          "Ratings & Reviews",
          "Suggestions & Feedback",
          "Settlements",
          "Invoices",
          "Refunds",
        ].map((tab) => {
          const tabValue = tab
            .toLowerCase()
            .replace(/ & /g, "-")
            .replace(/ /g, "-");
          return (
            <div
              key={tab}
              className={classNames("partner-tab px-3 py-2 me-3 fw-semibold", {
                active: activeTab === tabValue,
              })}
              style={{
                cursor: "pointer",
                borderBottom:
                  activeTab === tabValue
                    ? "3px solid #ffffffff"
                    : "3px solid transparent",
                color: activeTab === tabValue ? "#ffffffff" : "#6c757d",
                transition: "0.3s",
              }}
              onClick={() => setActiveTab(tabValue)}
            >
              {tab}
            </div>
          );
        })}
      </div>

      <div className="bg-white p-4 rounded-4 shadow-sm">
        {activeTab === "profile" && (
          <ProfileTab partnerDetails={partnerDetails} />
        )}
        {activeTab === "places" && <ListedPlacesTab partnerId={id} />}
        {activeTab === "gallery" && <GalleryTab partnerId={id} />}
        {activeTab === "ratings-reviews" && (
          <RatingsReviewsTab partnerId={id} />
        )}
        {activeTab === "suggestions-feedback" && !selectedFeedbackId && (
          <SuggestionsFeedbackTab
            partnerId={id}
            onChatClick={setSelectedFeedbackId}
          />
        )}
        {activeTab === "suggestions-feedback" && selectedFeedbackId && (
          <FeedbackChat
            feedbackId={selectedFeedbackId}
            onBack={() => setSelectedFeedbackId(null)}
          />
        )}
        {activeTab === "settlements" && <SettlementsTab partnerId={id} />}
        {activeTab === "invoices" && <InvoicesTab partnerId={id} />}
        {activeTab === "refunds" && <RefundsTab partnerId={id} />}
      </div>
    </div>
  );
};

export default PartnerDetails;
